<template>
  <div class="container-fluid">
    <Alert
      v-if="showAlert"
      :message="alertMessage"
      :alertType="alertType"
      :alertIcon="alertIcon"
      :closable="alertClosable"
      @onClose="dismissAlert"
      :isSelfService="isSelfService"
    />

    <br v-if="showAlert" />

    <div class="row">
      <!-- Left column -->
      <div class="col-md-6 bg-success ps-5 pt-4">
        <h1 class="pj-lime-green-header my-2">
          {{ t("GET_YOUR_PHONE_ON_CREDIT") }}
        </h1>
        <h2 class="h3 mb-4 pj-white-text">{{ t("IN_THREE_MINUTES") }}</h2>
        <p class="mb-4 pj-white-text">
          {{ t("PAYJOY_IS_AN_INSTITUTION") }}
        </p>
        <!-- Placeholder for the illustration -->
        <div>
          <img
            class="ecommerce-terms-and-privacy"
            :src="`${useAsset(
              'img/merchant-console/ecommerce_terms_and_privacy.svg'
            )}`"
            alt="Terms and Privacy"
          />
        </div>
      </div>

      <!-- Right column -->
      <div class="col-md-6">
        <div class="row">
          <h3 class="mb-4 mt-3 h3-ecommerce-green">
            {{ t("FINISH_YOUR_PURCHASE_IN_3") }}
          </h3>

          <ul class="list-unstyled">
            <li class="mb-3 d-flex flex-row align-items-start">
              <div class="pt-2 with-55">
                <img
                  :src="`${useAsset(
                    'img/merchant-console/validate_identity.svg'
                  )}`"
                  alt="icon"
                />
              </div>
              <div class="mx-2">
                <h4 class="h3-ecommerce">
                  {{ t("VALIDATE_YOUR_IDENTITY") }}
                </h4>
                <p>{{ t("KEEP_AT_HAND") }}</p>
              </div>
            </li>
            <li class="mb-3 d-flex flex-row align-items-start">
              <div class="pt-2 with-55">
                <img
                  class="d-inline"
                  :src="`${useAsset(
                    'img/merchant-console/choose_your_option.svg'
                  )}`"
                  alt="icon"
                />
              </div>
              <div class="mx-2">
                <h4 class="h3-ecommerce">
                  {{ t("CHOOSE_YOUR_FINANCE_OPTIONS") }}
                </h4>
                <p>{{ t("NEVER_PAY_LATE_FEES") }}</p>
              </div>
            </li>
            <li class="mb-3 d-flex flex-row align-items-start">
              <div class="pt-2 with-55">
                <img
                  :src="`${useAsset(
                    'img/merchant-console/do_down_payment.svg'
                  )}`"
                  alt="icon"
                />
              </div>
              <div class="mx-2">
                <h3 class="h3-ecommerce">
                  {{ t("MAKE_DOWN_PAYMENT_AND_GET") }}
                </h3>
                <p>{{ t("USE_ANY_CARD_FOR_DOWN_PAYMENT") }}</p>
              </div>
            </li>
          </ul>
        </div>

        <TermsAndPrivacyContentModals
          :styleForSelfService="isSelfService"
          :locale="customerLocaleCode"
          :cartTag="cartTag"
        />

        <div class="py-4 w-75 mx-auto">
          <ButtonContinue
            class="btn-next"
            :class="{ 'fs-3 pj-ss-btn': isSelfService }"
            :isEnabled="!acceptingPrivacyAndTerms"
            @callback="handleAcceptPrivacyAndTerms"
          >
            <i
              v-if="acceptingPrivacyAndTerms"
              class="fa fa-spin fa-spinner"
            ></i>
            <span v-else>{{ t("CONTINUE") }}</span>
          </ButtonContinue>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from "vue";
import { useI18n } from "@/apps/merchant3/composables/useI18n";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import useAsset from "@/apps/merchant3/shared-helpers/useAsset";
import { useTermsAndPrivacyAcceptance } from "@/apps/merchant3/hooks/useTermsAndPrivacyAcceptance";
import { useCustomerLocale } from "@/apps/merchant3/hooks/useCustomerLocale";
import { useProductVersion } from "@/apps/merchant3/hooks/useProductVersion";
import { useAlert } from "@/apps/merchant3/hooks/useAlert";
import Alert from "@/apps/merchant3/shared-components/common/Alert.vue";
import ButtonContinue from "@/apps/merchant3/shared-components/ui/ButtonContinue.vue";
import TermsAndPrivacyContentModals from "./TermsAndPrivacyContentModals.vue";
import { trackMarketingEvent } from "@/apps/merchant3/services/marketing-events.service";
import MarketingEvents from "@/apps/merchant3/types/MarketingEvents";

const { t } = useI18n();
const store = useStore();
const router = useRouter();

const { isSelfService } = useProductVersion();
const { customerLocaleCode } = useCustomerLocale();
const cartTag = computed(() => store.state.carts.cartTag);

const {
  alertClosable,
  alertIcon,
  alertMessage,
  alertType,
  dismissAlert,
  showAlert,
} = useAlert();

alertType.value = "alert-danger";

const {
  acceptPrivacyAndTerms,
  acceptingPrivacyAndTerms,
  handleContinueToNextPage,
} = useTermsAndPrivacyAcceptance();

const handleAcceptPrivacyAndTerms = () => {
  acceptPrivacyAndTerms(
    cartTag.value,
    () => {
      trackMarketingEvent(MarketingEvents.TERMS_AND_CONDITIONS_ACCEPTED);
      handleContinueToNextPage();
    },
    (message, _error) => {
      alertMessage.value = message;
      showAlert.value = true;
    }
  );
};

const configureLanguageForEcommerce = () => {
  const currentLang = localStorage.getItem("lang") ?? undefined;
  const preferredLanguage = customerLocaleCode.value;

  if (currentLang === undefined || currentLang !== preferredLanguage) {
    localStorage.setItem("lang", preferredLanguage);
    router.go(0);
  }
};

onMounted(() => {
  configureLanguageForEcommerce();
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.content {
  border: 1px solid $pj-color-green;
  height: 50vh;
  overflow-y: auto;
  text-align: left;
  border-radius: 8px;
  font-size: 14px;
}
.with-55 {
  width: 51px;
}
.h3-ecommerce {
  font-size: 1.2rem;
  color: $pj-color-dark;
}
.h3-ecommerce-green {
  font-size: 1.2rem;
  color: $pj-color-green;
}
.pj-white-text {
  color: $pj-color-white;
}
@media (max-width: 767px) {
  .ecommerce-terms-and-privacy {
    display: none;
  }
}
</style>
